/**
 * 提问题面（原站 ut 组件 / ZeN5PQ）。
 *
 * 行为细节（实测自 bundle）：
 *  - 选项组在文字逐字显示结束后才出现（onEnd -> setAnswerVisible(true)）
 *  - 若某一选项带 hide 标记，则该选项延迟 60s 才出现（setTimeout(...,6e4)）
 *  - 点击选项后立即禁用全部按钮，等 600ms 再提交（setTimeout(...,600)）
 */
import { useEffect, useRef, useState } from 'react';
import type { AnswerKey, Quest } from '../../types';
import { AnswerButton } from './AnswerButton';
import { Typewriter } from './Typewriter';
import './dialogue.css';

/** 隐藏选项的等待时长（原站硬编码 6e4） */
const HIDDEN_OPTION_DELAY = 60_000;
/** 提交前的延迟（原站硬编码 600） */
const SUBMIT_DELAY = 600;

export interface QuestionPanelProps {
  quest: Quest;
  qId: string;
  onSubmit: (qId: string, aId: AnswerKey) => void;
}

export function QuestionPanel({ quest, qId, onSubmit }: QuestionPanelProps) {
  const [contentDone, setContentDone] = useState(false);
  const [revealHidden, setRevealHidden] = useState(false);
  const [pending, setPending] = useState(false);
  const timer = useRef<number | null>(null);

  const hasHidden = quest.options.some((o) => o.hide);

  useEffect(() => {
    setContentDone(false);
    setRevealHidden(false);
    setPending(false);
  }, [qId]);

  useEffect(() => {
    if (!hasHidden) return;
    timer.current = window.setTimeout(() => setRevealHidden(true), HIDDEN_OPTION_DELAY);
    return () => void (timer.current && window.clearTimeout(timer.current));
  }, [hasHidden, qId]);

  const choose = (key: AnswerKey) => {
    setPending(true);
    timer.current = window.setTimeout(() => onSubmit(qId, key), SUBMIT_DELAY);
  };

  return (
    <div className="question">
      <Typewriter text={quest.content} onEnd={() => setContentDone(true)} />
      {quest.options.length > 0 && (
        <div className="answerGroup" data-phase={contentDone ? 'entered' : 'entering'}>
          {quest.options.map((opt) => {
            // 带 hide 的选项到点才渲染
            if (opt.hide && !revealHidden) return null;
            return (
              <div
                className="answerWrapper"
                data-phase={contentDone ? 'entered' : 'entering'}
                key={opt.key}
              >
                <AnswerButton text={opt.content} disabled={pending} onClick={() => choose(opt.key)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
